import { Copy, Minus, Pencil, Plus, Trash2, Undo2 } from 'lucide-react'
import { useId, useState } from 'react'

import { Button } from '@krp/ui-kit'

import type { CartLineVm } from '../port/types'
import { PriceField } from './PriceField'

/**
 * Полоса правки позиции — как в старом РМК (кассовое место 1С): выбранная
 * строка чека крупно — имя, штрихкод (копировать), цена, количество −/+ и
 * «Удалить». Цену позиции даёт каталог кассы; с правом `sales.price` кассир
 * ставит свою прямо здесь, как в 1С: любую, без причины (RP-FB8, решение
 * владельца 13.10.2026). Поле цены — общее со строкой таблицы (`PriceField`):
 * фокус выделяет всё, цена применяется по Enter или уходу из поля, Esc —
 * отмена набора; «Цена кабинета» возвращает цену каталога.
 */
export function LineEditor({
  line,
  locked,
  canEditPrice = false,
  onPrice,
  onResetPrice,
  onPriceDone,
  onQuantity,
  onIncrement,
  onDecrement,
  onRemove,
}: {
  line: CartLineVm
  locked: boolean
  canEditPrice?: boolean
  onPrice?: (value: string) => void
  onResetPrice?: () => void
  /** Enter в поле цены: цена применена — фокус обратно в строку поиска (сканер печатает туда). */
  onPriceDone?: () => void
  onQuantity: (value: string) => void
  onIncrement: () => void
  onDecrement: () => void
  onRemove: () => void
}) {
  const id = useId()
  const priceId = `${id}-price`
  const errorId = `${id}-error`
  const error = line.quantityError ?? line.priceError
  // Своей цены нет — в поле цена кабинета: с неё и начинают набор, как в кассе 1С.
  const savedPrice = line.price ?? line.catalogPrice ?? ''
  const [copied, setCopied] = useState(false)
  const copy = () => {
    if (line.barcode === null || typeof navigator === 'undefined' || navigator.clipboard === undefined) return
    void navigator.clipboard.writeText(line.barcode).then(
      () => setCopied(true),
      () => setCopied(false),
    )
  }
  return (
    <div className="rmk-editor" role="group" aria-label={`Позиция: ${line.name}`} data-invalid={error !== null || undefined}>
      <div className="rmk-editor__identity">
        <span className="rmk-editor__name" title={line.name}>
          {line.name}
        </span>
        <span className="rmk-editor__code">
          <span className="rmk-editor__code-text">Штрихкод: {line.barcode ?? '—'}</span>
          {line.barcode !== null && (
            <Button
              variant="ghost"
              aria-label={copied ? 'Штрихкод скопирован' : 'Скопировать штрихкод'}
              title={copied ? 'Скопирован' : 'Скопировать'}
              icon={<Copy size={13} aria-hidden="true" />}
              onClick={copy}
            />
          )}
        </span>
      </div>
      {canEditPrice && onPrice !== undefined ? (
        <div className="rmk-editor__field">
          <label htmlFor={priceId}>{line.price !== null ? 'Своя цена, ₽' : 'Цена, ₽'}</label>
          <PriceField
            id={priceId}
            className="rmk-stepper__input rmk-editor__price"
            saved={savedPrice}
            disabled={locked}
            invalid={line.priceError !== null}
            describedBy={line.priceError !== null ? errorId : undefined}
            onCommit={onPrice}
            onDone={onPriceDone}
          />
          {line.price !== null && (
            <span className="rmk-editor__catalog">
              <Pencil size={13} aria-hidden="true" /> {line.catalogPriceText ?? 'своя цена'}
              {line.priceChangedBy !== null && `, поставил(а) ${line.priceChangedBy}`}
            </span>
          )}
          {line.price !== null && onResetPrice !== undefined && (
            <Button variant="ghost" icon={<Undo2 size={15} aria-hidden="true" />} disabled={locked} onClick={onResetPrice}>
              Цена кабинета
            </Button>
          )}
        </div>
      ) : (
        <div className="rmk-editor__field">
          <span>Цена</span>
          <b title={line.catalogPriceText ?? 'Цену даёт каталог кассы'}>
            {line.priceText ?? 'даст касса'}
            {line.price !== null && ' (своя)'}
          </b>
        </div>
      )}
      <div className="rmk-editor__field">
        <label htmlFor={id}>{line.unit === null ? 'Количество' : `Количество, ${line.unit}`}</label>
        <div className="rmk-stepper">
          <button
            type="button"
            aria-label={`Меньше: ${line.name}`}
            disabled={locked || line.isWeight}
            onClick={onDecrement}
          >
            <Minus size={16} aria-hidden="true" />
          </button>
          <input
            id={id}
            className="rmk-stepper__input"
            inputMode={line.isWeight ? 'decimal' : 'numeric'}
            value={line.quantity}
            disabled={locked}
            aria-invalid={line.quantityError !== null}
            aria-describedby={line.quantityError !== null ? errorId : undefined}
            onChange={(event) => onQuantity(event.target.value)}
          />
          <button
            type="button"
            aria-label={`Больше: ${line.name}`}
            disabled={locked || line.isWeight}
            onClick={onIncrement}
          >
            <Plus size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
      <Button
        variant="ghost"
        icon={<Trash2 size={16} aria-hidden="true" />}
        aria-label={`Удалить позицию ${line.name}`}
        disabled={locked}
        onClick={onRemove}
      >
        Удалить
      </Button>
      {error !== null && (
        <p id={errorId} className="rmk-editor__error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
